export interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  imageEmoji: string;
  isPromoFree?: boolean;
}

import { FlightRecorder } from './FlightRecorder';

export const appFlightRecorder = new FlightRecorder(20);

export interface CartItem {
  product: Product;
  quantity: number;
}

export interface Order {
  id: string;
  date: string;
  items: CartItem[];
  total: number;
  status: 'delivered' | 'processing' | 'shipped';
}

export interface TargetAppState {
  products: Product[];
  cart: CartItem[];
  appliedDiscounts: string[];
  orders: Order[] | null;
  customerName: string;
  customerEmail: string;
  isBug1Active: boolean; // Bug 1: NaN calculation
  isBug2Active: boolean; // Bug 2: Unclamped negative discount
  isBug3Active: boolean; // Bug 3: Orders null pointer map crash
  isCalcBugActive: boolean; // Real-world: ahfarmer/calculator 5 ÷ 0
  lastError: string | null;
  runtimePatchesApplied: Record<string, boolean>;
}

export const INITIAL_PRODUCTS: Product[] = [
  {
    id: 'cyber-keyboard',
    name: 'Cyberdeck Optical Keyboard',
    price: 149,
    category: 'Hardware',
    imageEmoji: '⌨️',
  },
  {
    id: 'anc-headphones',
    name: 'Spatial Studio ANC Headphones',
    price: 199,
    category: 'Audio',
    imageEmoji: '🎧',
  },
  {
    id: 'curved-monitor',
    name: 'Curved 4K Ultra OLED Display',
    price: 499,
    category: 'Display',
    imageEmoji: '🖥️',
  },
  {
    id: 'free-stickers',
    name: 'DevPack Holographic Stickers',
    price: 0,
    category: 'Swag',
    imageEmoji: '✨',
    isPromoFree: true,
  },
];

class TargetAppStore {
  private state: TargetAppState = {
    products: INITIAL_PRODUCTS,
    cart: [
      { product: INITIAL_PRODUCTS[0], quantity: 1 },
      { product: INITIAL_PRODUCTS[3], quantity: 1 }, // Includes free $0 promo sticker!
    ],
    appliedDiscounts: [],
    orders: [
      {
        id: 'ORD-9821',
        date: '2026-08-28',
        items: [{ product: INITIAL_PRODUCTS[1], quantity: 1 }],
        total: 199,
        status: 'delivered',
      },
    ],
    customerName: 'Alex Rivera',
    customerEmail: 'alex.rivera@techcorp.io',
    isBug1Active: false,
    isBug2Active: false,
    isBug3Active: false,
    isCalcBugActive: false,
    lastError: null,
    runtimePatchesApplied: {},
  };

  private listeners: Set<() => void> = new Set();

  public getState(): TargetAppState {
    return { ...this.state };
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(action: string = 'state_update') {
    appFlightRecorder.record(action, this.state);
    this.listeners.forEach((listener) => listener());
  }

  public addToCart(product: Product) {
    const existing = this.state.cart.find((i) => i.product.id === product.id);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.state.cart.push({ product, quantity: 1 });
    }
    this.notify(`addToCart(${product.id})`);
  }

  public removeFromCart(productId: string) {
    this.state.cart = this.state.cart.filter((i) => i.product.id !== productId);
    this.notify(`removeFromCart(${productId})`);
  }

  public updateQuantity(productId: string, quantity: number) {
    if (quantity <= 0) {
      this.removeFromCart(productId);
      return;
    }
    const item = this.state.cart.find((i) => i.product.id === productId);
    if (item) {
      item.quantity = quantity;
      this.notify(`updateQuantity(${productId}, ${quantity})`);
    }
  }

  public applyDiscount(code: string) {
    const clean = code.trim().toUpperCase();
    if (clean && !this.state.appliedDiscounts.includes(clean)) {
      this.state.appliedDiscounts.push(clean);
      this.notify(`applyDiscount(${code})`);
    }
  }

  public removeDiscount(code: string) {
    this.state.appliedDiscounts = this.state.appliedDiscounts.filter((d) => d !== code);
    this.notify(`removeDiscount(${code})`);
  }

  public setCustomerInfo(name: string, email: string) {
    this.state.customerName = name;
    this.state.customerEmail = email;
    this.notify(`setCustomerInfo(${name})`);
  }

  // --- Bug Injections & Patch Controls ---
  public setBugState(bugId: 'bug-1' | 'bug-2' | 'bug-3', active: boolean) {
    if (bugId === 'bug-1') this.state.isBug1Active = active;
    if (bugId === 'bug-2') this.state.isBug2Active = active;
    if (bugId === 'bug-3') {
      this.state.isBug3Active = active;
      if (active) {
        // Corrupt orders to null to cause map crash
        this.state.orders = null as any;
      } else {
        this.state.orders = [
          {
            id: 'ORD-9821',
            date: '2026-08-28',
            items: [{ product: INITIAL_PRODUCTS[1], quantity: 1 }],
            total: 199,
            status: 'delivered',
          },
        ];
      }
    }
    this.notify(`setBugState(${bugId}, ${active})`);
  }

  public applyRuntimePatch(patchId: string) {
    this.state.runtimePatchesApplied[patchId] = true;
    if (patchId === 'patch-bug-1') this.state.isBug1Active = false;
    if (patchId === 'patch-bug-2') this.state.isBug2Active = false;
    if (patchId === 'patch-bug-3') {
      this.state.isBug3Active = false;
      this.state.orders = [];
    }
    if (patchId === 'patch-calc-div-zero') {
      this.state.isCalcBugActive = false;
    }
    this.notify(`applyRuntimePatch(${patchId})`);
  }

  public resetAll() {
    this.state.isBug1Active = false;
    this.state.isBug2Active = false;
    this.state.isBug3Active = false;
    this.state.isCalcBugActive = false;
    this.state.lastError = null;
    this.state.runtimePatchesApplied = {};
    this.state.appliedDiscounts = [];
    this.state.orders = [
      {
        id: 'ORD-9821',
        date: '2026-08-28',
        items: [{ product: INITIAL_PRODUCTS[1], quantity: 1 }],
        total: 199,
        status: 'delivered',
      },
    ];
    this.state.cart = [
      { product: INITIAL_PRODUCTS[0], quantity: 1 },
      { product: INITIAL_PRODUCTS[3], quantity: 1 },
    ];
    appFlightRecorder.clear();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('calc-reset-fault'));
    }
    this.notify('resetAll');
  }

  public setLastError(err: string | null) {
    this.state.lastError = err;
    this.notify(`setLastError(${err})`);
  }
}

export const targetAppStore = new TargetAppStore();
